<script setup>
import { initialAbility } from '@/plugins/casl/ability'
import { useAppAbility } from '@/plugins/casl/useAppAbility'
import { storeToRefs } from 'pinia'

import {useRestaurantsStore, useUserGroupsStore, useUsersStore, useVarsStore, useSupportStore} from '@/stores'
import {exitSimulation, simulation} from "@/plugins/misc"
import appStoreImage from "@/assets/app-store.svg"
import NavBarNotifications from "@/layouts/components/NavBarNotifications.vue"
import rmLogo from "@/assets/logo.svg"

const isUserLoggedIn = localStorage.getItem('userData') ? true : false
const userLoggedInAndNotDummy = isUserLoggedIn && JSON.parse(localStorage.getItem('userData')).email.includes('@')

const usersStore = useUsersStore()
let myUser = null
let isRunningInIOSAppStore = null
let loginDialogMode = null
let canActForMembers = null;
({ myUser, isRunningInIOSAppStore, loginDialogMode, canActForMembers} = storeToRefs(usersStore))

// RM-159: only members the actor can actually simulate appear in the acting-as
// switcher. Tier 1 spouses (canSimulate === false) submit reservations but
// can't "Manage Account", so they're filtered out here.
const simulatableMembers = computed(() => (canActForMembers.value || [])
  .filter(m => m.canSimulate !== false))
if(isUserLoggedIn){
  usersStore.getMyUser()
  usersStore.getFollowers()
  usersStore.setupMyUserListeners()

  // RM-150: load the list of members the actor can act on behalf of, so the
  // Acting-as switcher in this menu can render without a delay on first open.
  if (JSON.parse(localStorage.getItem('userData'))?.userLevel !== 'admin') {
    usersStore.fetchCanActForMembers()
  }
}

const startActingAs = email => {
  simulation(email)
}

const varsStore = useVarsStore()
let editingGlobalSettings = null;
({ editingGlobalSettings } = storeToRefs(varsStore))

const userGroupsStore = useUserGroupsStore()
let userGroups = null
let restaurantUserGroups = null;
({  userGroups, restaurantUserGroups  } = storeToRefs(userGroupsStore))

const restaurantsStore = useRestaurantsStore()
restaurantsStore.getRestaurants()

const supportStore = useSupportStore()
const { adminTotalUnread: supportUnread } = storeToRefs(supportStore)

let fetched = false
let userGroupCount = 0

// Watch for changes in myUser
watch(myUser, newMyUser => {
  if(newMyUser){
    // If myUser is not null, call getAllUserGroups()
    if (newMyUser.userLevel != 'admin' && !fetched){
      userGroupsStore.getMyUserGroups()

      // usersStore.getAllUsers();
      fetched = true
    }

    //
    else if(userGroupCount != newMyUser.userGroups?.length){
      userGroupCount = newMyUser.userGroups?.length
      userGroupsStore.getMyUserGroups()
    }
  }
}, { immediate: true })


const router = useRouter()
const ability = useAppAbility()


const isSimulation = !!localStorage.getItem('simulation')

const openSupport = async () => {
  try {
    const conv = await supportStore.getOrCreateSupportConversation()
    if (conv?._id) {
      router.push({ path: '/messages', query: { conv: conv._id } })
    }
  } catch (err) {
    console.error('Failed to open support thread', err)
  }
}

const logout = () => {
  localStorage.removeItem('userData')
  localStorage.removeItem('accessToken')
  localStorage.removeItem('jwt')
  localStorage.removeItem('simulation')
  localStorage.removeItem('simulationStack')
  localStorage.removeItem('simulationHref')
  localStorage.removeItem('simulationScope')
  localStorage.removeItem('adminJwt')
  location.href="/auth/logout?loc=4"
}

const avatarBadgeProps = {
  dot: true,
  location: 'bottom right',
  offsetX: 3,
  offsetY: 3,
  color: 'success',
  bordered: true
}

const groupedRestaurantUserGroups = computed(() => {
  let temp =  restaurantUserGroups.value.reduce((groups, restaurantUserGroup) => {
    const key = restaurantUserGroup.restaurant.market
    if (!groups[key]) {
      groups[key] = []
    }
    groups[key].push(restaurantUserGroup)
    
    return groups
  }, {})

  for (let market in temp) {
    temp[market].sort((a, b) => {
      let nameA = a.name.toUpperCase() // ignore upper and lowercase
      let nameB = b.name.toUpperCase() // ignore upper and lowercase
      if (nameA < nameB) {
        return -1
      }
      if (nameA > nameB) {
        return 1
      }
      
      return 0 // names must be equal
    })
  }

  return temp

})
</script>

<template>
  <VBtn
    width="40"
    height="40"
    class="cursor-pointer"
    color="secondary"
    variant="plain"
    icon
  >
    <VIcon icon="mdi-menu" />
    <!-- SECTION Menu -->
    <VMenu
      activator="parent"
      location="bottom end"
      offset="14px"
    >
      <VList>
        <!-- 👉 User Avatar & Name -->
        <VListItem v-if="!userLoggedInAndNotDummy">
          <template #prepend>
            <VListItemAction>
              <VRow
                align="center"
                class="pa-2 py-1"
              >
                <VCol>
                  <VBtn
                    variant="flat"
                    class=""
                    size="small"
                    color="primary"
                    @click="loginDialogMode = 'activate'"
                  >
                    <span>{{ $t('menu.requestToJoin') }}</span>
                  </VBtn>
                  <VBtn
                    variant="flat"
                    class="ml-2"
                    size="small"
                    color="#efefef"
                    @click="loginDialogMode = 'login'"
                  >
                    <span>{{ $t('menu.logIn') }}</span>
                  </VBtn>
                </VCol>
              </VRow>
            </VListItemAction>
          </template>
        </VListItem>
        <VDivider
          v-if="!userLoggedInAndNotDummy"
          class="my-2"
        />

        <VListItem
          v-else
          to="/home"
        >
          <template #prepend>
            <img
              :src="rmLogo"
              height="22"
              class="me-2"
            >
          </template>
          <VListItemTitle>{{ $t('menu.home') }}</VListItemTitle>
        </VListItem>
        <VDivider class="my-2" />

        <VListItem to="/members">
          <VListItemTitle>{{ $t('menu.howRmWorks') }}</VListItemTitle>
        </VListItem>

        <VListItem to="/luxurypartners">
          <VListItemTitle>{{ $t('menu.luxuryPartners') }}</VListItemTitle>
        </VListItem>

        <VListItem
          v-if="userLoggedInAndNotDummy && !isSimulation"
          to="/membership"
        >
          <VListItemTitle>{{ $t('menu.membershipDetails') }}</VListItemTitle>
        </VListItem>

        <VListItem to="/preferred">
          <VListItemTitle>{{ $t('menu.preferredRestaurants') }}</VListItemTitle>
        </VListItem>

        <VListItem
          v-if="userLoggedInAndNotDummy"
          to="/settings/notifications"
        >
          <VListItemTitle>Notification Preferences</VListItemTitle>
        </VListItem>

        <VListItem
          v-if="myUser?.activeSubscription && !myUser?.isSecondaryAccount && myUser?.isPlatinum && myUser?.primaryGroupPermission != 'adminStandard' && !isSimulation"
          @click="usersStore.showSecondaryMemberDialog()"
        >
          <VListItemTitle>{{ $t('menu.secondaryAccount') }}</VListItemTitle>
          <VListItemSubtitle v-if="myUser?.secondaryAccountEmail">
            {{ myUser.secondaryAccountEmail }}
          </VListItemSubtitle>
          <VListItemSubtitle
            v-if="myUser?.secondaryAccountEmail"
            class="text-caption"
          >
            (Invited)
          </VListItemSubtitle>
        </VListItem>

        <VListItem
          v-if="myUser?.activeSubscription && !myUser?.isSecondaryAccount && myUser?.isPlatinum"
          @click="usersStore.showPersonalAdminsDialog()"
        >
          <VListItemTitle>Admin Account</VListItemTitle>
          <VListItemSubtitle class="text-caption">
            Authorize others to submit requests on your behalf
          </VListItemSubtitle>
        </VListItem>

        <!--
          RM-150: Acting-as switcher. Shows when the actor is a group admin
          or active personal admin for one or more members. Hidden for
          platform admins (they use the AdminDirectReservationDialog autocomplete)
          and during an existing simulation session. 
        -->
        <template v-if="!isSimulation && simulatableMembers && simulatableMembers.length > 0">
          <VDivider class="my-2" />
          <div class="text-caption ml-4 mt-2">
            Manage on behalf of
          </div>
          <VListItem
            v-for="m in simulatableMembers"
            :key="m.email"
            @click="startActingAs(m.email)"
          >
            <VListItemTitle>Manage {{ m.firstName }}&rsquo;s Account</VListItemTitle>
            <VListItemSubtitle class="text-caption">
              {{ m.email }} &middot; {{ m.relationship === 'spouse' ? 'Spouse/Partner' : (m.relationship === 'personal-admin' ? 'Admin account' : 'Group admin') }}
            </VListItemSubtitle>
          </VListItem>
        </template>

        <section v-if="myUser?.userLevel == 'admin' || (myUser?.primaryGroupType == 'restaurant' && (myUser?.primaryGroupPermission == 'owner' || myUser?.primaryGroupPermission == 'admin' || myUser?.primaryGroupPermission == 'adminStandard'))">
          <VDivider class="my-2" />
          <VDivider class="my-2" />
          <VListItem to="/reservations-review">
            <VListItemTitle>{{ $t('menu.activityBookings') }}</VListItemTitle>
          </VListItem>
        </section>

        <section v-if="myUser?.userLevel != 'admin' && restaurantUserGroups?.length > 0">
          <VDivider class="my-2" />
          <div class="text-caption ml-4">
            {{ $t('menu.restaurantPages') }}
          </div>
          <template
            v-for="(group, market) in groupedRestaurantUserGroups"
            :key="market"
          >
            <span
              v-if="Object.keys(groupedRestaurantUserGroups).length > 1"
              class="text-caption ml-4"
            >{{ market }}</span>
            <template
              v-for="(restaurantUserGroup, index) in group"
              :key="index"
            >
              <VListItem :to="`/r/${restaurantUserGroup.restaurant.profileId}`">
                <VListItemTitle>{{ restaurantUserGroup.name }}</VListItemTitle>
              </VListItem>
            </template>
          </template>
        </section>
        <section v-if="myUser?.primaryGroupType != 'restaurant' && (myUser?.primaryGroupPermission == 'owner' || myUser?.primaryGroupPermission == 'admin' || myUser?.primaryGroupPermission == 'adminStandard')">
          <template
            v-for="(userGroup, index) in userGroups"
            :key="index"
          >
            <VDivider class="my-2" />
            <span class="text-caption ml-4">{{ userGroup.name }}</span>
            <VListItem
              v-if=" myUser?.primaryGroupPermission == 'owner' && myUser?.primaryGroupType == 'restaurant'"
              @click="restaurantsStore.adminEditRestaurantByGroup(userGroup._id)"
            >
              <VListItemTitle>{{ $t('menu.profileAndSettings') }}</VListItemTitle>
            </VListItem>
            <VListItem
              v-else-if=" myUser?.primaryGroupPermission == 'owner' && myUser?.isPlatinum"
              @click="userGroupsStore.adminEditUserGroup(userGroup._id)"
            >
              <VListItemTitle>{{ $t('menu.profileAndSettings') }}</VListItemTitle>
            </VListItem>
            <VListItem to="/group-members">
              <VListItemTitle>{{ $t('menu.members') }}</VListItemTitle>
            </VListItem>
            <VListItem
              @click="openSupport"
            >
              <VListItemTitle>{{ $t('menu.support') }}</VListItemTitle>
            </VListItem>
          </template>
        </section>

        <section v-if="myUser?.userLevel == 'admin'">
          <VDivider class="my-2" />
          <VDivider class="my-2" />
          <span class="text-caption ml-4">{{ $t('menu.adminSettings') }}</span>
          <VListItem to="/all-members">
            <VListItemTitle>{{ $t('menu.memberAccounts') }}</VListItemTitle>
          </VListItem>
          <VListItem to="/all-groups">
            <VListItemTitle>{{ $t('menu.groupAccounts') }}</VListItemTitle>
          </VListItem>
          <VListItem to="/admin-applications">
            <VListItemTitle>Applications</VListItemTitle>
          </VListItem>
          <VListItem to="/all-restaurants">
            <VListItemTitle>{{ $t('menu.restaurantDatabase') }}</VListItemTitle>
          </VListItem>
          <VListItem to="/all-tags">
            <VListItemTitle>{{ $t('menu.tags') }}</VListItemTitle>
          </VListItem>
          <VListItem to="/admin-activity-feed">
            <VListItemTitle>Activity Feed</VListItemTitle>
          </VListItem>
          <VListItem to="/admin-comms">
            <VListItemTitle>Comms Outbox</VListItemTitle>
          </VListItem>
          <VListItem to="/admin-comms-suppressions">
            <VListItemTitle>Email Suppressions</VListItemTitle>
          </VListItem>
          <VListItem to="/admin-reports">
            <VListItemTitle>Restaurant Reports</VListItemTitle>
          </VListItem>
          <VListItem to="/admin-sms-members">
            <VListItemTitle>RM via Text</VListItemTitle>
          </VListItem>
          <VListItem to="/admin-member-highlights">
            <VListItemTitle>Member Highlights</VListItemTitle>
          </VListItem>
          <VListItem to="/admin-support">
            <VListItemTitle>
              Support Inbox
              <VBadge
                v-if="supportUnread > 0"
                :content="supportUnread"
                color="error"
                inline
                class="ml-1"
              />
            </VListItemTitle>
          </VListItem>
          <VListItem @click="editingGlobalSettings = true">
            <VListItemTitle>{{ $t('menu.settings') }}</VListItemTitle>
          </VListItem>
        </section>

        <VDivider class="my-2" />
        <!-- 👉 Logout -->
        <VListItem
          v-if="userLoggedInAndNotDummy"
          @click="logout"
        >
          <VListItemTitle>{{ $t('menu.logout') }}</VListItemTitle>
        </VListItem>
        <VListItem
          v-if="isSimulation"
          @click="exitSimulation"
        >
          <VListItemTitle>{{ $t('menu.exitSimulation') }}</VListItemTitle>
        </VListItem>
        <template v-if="!isRunningInIOSAppStore">
          <VDivider class="my-2" />
          <VListItem>
            <a
              v-if="!isRunningInIOSAppStore"
              href="https://apps.apple.com/us/app/rm-preferred-reservations/id6475874605"
              target="_blank"
            >
              <VImg
                :src="appStoreImage"
                class="mt-1"
                height="30"
              />
            </a>
          </VListItem>
        </template>
      </VList>
    </VMenu>
    <!-- !SECTION -->
  </VBtn>
</template>
